<template>
  <div
    class="app-root"
    :class="{
      small: isSmall,
      loading: isLoading,
      geolocation: isUsingGeolocation,
      'geolocation-loading':
        appState === 'write' && isUsingGeolocation && !geolocationCoordsArray,
      osmOverlay: currentLayer === 'OpenStreetMap',
      aerofoto: currentLayer === 'Aerofoto',
      'is-read-mode': appState === 'read',
      'is-write-mode': appState === 'write',
      'is-save-mode': appState === 'save',
    }"
  >
    <section class="overlay">
      <strong v-if="isLoading"> Laadimine ⏳ </strong>
      <strong v-else>Positsioneerimine 🛰️</strong>
    </section>

    <InfoPopup :isOpen="showInfo" @close="showInfo = false" />

    <!-- Menüü -->
    <header>
      <label v-for="item in headerMenu" @click="isLoading = true">
        <input
          type="radio"
          v-model="appState"
          :value="item.value"
          :disabled="appState === 'save'"
        />
        <div>{{ item.label }}</div>
      </label>

      <button class="info-button" @click="showInfo = true">
        <svg viewBox="0 0 24 24">
          <path
            fill="#262626"
            d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
          />
        </svg>
      </button>
    </header>

    <!-- Paneel -->
    <aside ref="asideElement" @scroll="onAsideScroll">
      <template v-if="appState === 'write'">
        <button
          @click="appState = 'save'"
          class="big-button"
          :disabled="isUsingGeolocation && !geolocationCoordsArray"
        >
          Märgi punkt
        </button>

        <button
          v-if="geolocationPPError"
          @click="openInNewTab"
          class="big-button"
          style="margin: auto; font-size: 1.55em"
        >
          🛰️ GPS kasutamiseks vajuta siia
        </button>

        <small v-if="geolocationError" class="geolocation-error">
          [{{ geolocationError.code }}] {{ geolocationError.message }}
        </small>
      </template>

      <template v-else-if="appState === 'save'">
        <section class="save-form">
          <h2>Märgi punkt</h2>

          <strong>
            <small>Koordinaadid:</small>
            <span>{{ userFilledData.lat }}, {{ userFilledData.lng }}</span>
          </strong>
          <strong>
            <small>Piirkond:</small>
            <span>{{ userFilledData.county }}</span>
          </strong>

          <label class="category-input">
            <span>Kategooria</span>
            <select v-model="userFilledData.category_id">
              <option v-for="{ id, title } in categoriesData" :value="id">
                {{ title }}
              </option>
            </select>
          </label>

          <img v-if="imageDataBase64" :src="imageDataBase64" />

          <label v-else class="image-input">
            <b>Lisa pilt</b>
            <input type="file" accept="image/*" @change="onFileInput" />
          </label>

          <label class="title-input">
            <input
              type="text"
              v-model="userFilledData.title"
              placeholder="Pealkiri"
            />
          </label>

          <label class="free-text">
            <textarea
              v-model="userFilledData.description"
              placeholder="Kirjeldus"
              rows="5"
              maxlength="500"
            >
            </textarea>
          </label>

          <small> Pealkiri, pilt ja kirjeldus ei ole kohustuslikud! </small>

          <button @click="submitMarker" class="submit">Märgi</button>

          <button
            @click="cancelMarking"
            style="align-self: end; background-color: #ce4656"
          >
            Katkesta
          </button>
        </section>
      </template>

      <template v-else-if="markersData">
        <!-- Maakondade valik -->
        <section>
          <label class="dropdown">
            <span> Valitud piirkond </span>
            <select v-model="selectedCounty">
              <option value="k6ik">Kogu Eesti</option>
              <option v-for="(val, key, i) in countysOnMap" :value="key">
                {{ key }}
              </option>
            </select>
          </label>

          <label class="dropdown">
            <span> Märkimise aeg </span>
            <select v-model="selectedDate">
              <option v-for="date in allDates" :value="date">
                {{ date }}
              </option>
              <option value="n2dal">Viimane nädal</option>
              <option value="k6ik">Kõik päevad</option>
            </select>
          </label>

          <div class="legend" v-if="categoriesData">
            <div
              v-for="{ id, title, description, color } in categoriesData"
              :style="{
                '--c': color,
              }"
            >
              <strong>{{ title }}</strong>
            </div>

            <div
              :style="{
                '--c': fixedGreenColor,
              }"
            >
              <strong>Kordatehtud</strong>
            </div>
          </div>
        </section>

        <!-- Kaardid -->
        <MarkerCard
          v-for="item in markersDataFiltered"
          :item="item"
          @show="showMarkerOnMap(item)"
          @fix="markMarkerFixed(item)"
        />

        <section class="total">
          kokku märgitud
          {{
            `${markersDataFiltered.length} punkt${
              markersDataFiltered.length === 1 ? "" : "i"
            } (${selectedCounty === "k6ik" ? "Kogu Eesti" : selectedCounty})`
          }}
        </section>
      </template>
    </aside>

    <!-- Leaflet -->
    <main class="map" ref="mapContainer">
      <section v-if="appState === 'write' && !geolocationPPError">
        <label>
          <input type="checkbox" v-model="isUsingGeolocation" />
          <svg viewBox="0 0 28 36">
            <path
              d="M14 0c7.732 0 14 5.641 14 12.6C28 23.963 14 36 14 36S0 24.064 0 12.6C0 5.641 6.268 0 14 0Z"
            />
            <circle cx="14" cy="14" r="5.55" fill="white" />
          </svg>
          <div>Minu asukoht</div>
        </label>
      </section>
    </main>

    <!-- Footer -->
    <footer>
      <div>
        <a href="https://www.postimees.ee/" target="_blank">
          <PM /><span>Postimees</span>
        </a>
      </div>
      <div>
        <a href="https://leafletjs.com/" target="_blank">Leaflet</a>
        <a
          v-if="currentLayer === 'OpenStreetMap'"
          href="https://www.openstreetmap.org/"
          target="_blank"
        >
          OpenStreetMap
        </a>
        <a v-else href="https://maaamet.ee/" target="_blank">Maa-amet</a>
      </div>
    </footer>

    <!-- Arrow buttons -->
    <template v-if="isSmall && appState === 'read'">
      <div
        class="arrow-button arrow-left"
        @click="scrollLeft"
        v-if="showArrowLeft"
      >
        <svg
          viewBox="0 0 12 12"
          style="transform: rotate(90deg); fill: currentColor"
        >
          <path d="M6,9.2L0.6,3.9l0.7-0.7L6,7.8l4.6-4.6l0.7,0.7L6,9.2z"></path>
        </svg>
      </div>
      <div
        class="arrow-button arrow-right"
        @click="scrollRight"
        v-if="showArrowRight"
      >
        <svg
          viewBox="0 0 12 12"
          style="transform: rotate(270deg); fill: currentColor"
        >
          <path d="M6,9.2L0.6,3.9l0.7-0.7L6,7.8l4.6-4.6l0.7,0.7L6,9.2z"></path>
        </svg>
      </div>
    </template>
  </div>
</template>

<style scoped>
.app-root {
  --section-color: #0064fa;
  --section-secondary-color: #0000ba;
  --section-tertiary-color: #a5e6e9;
  --line: #d5d8dd;
  --gray: #717c8e;
  --lightest: #eef3f6;
  --text-color: #262626;
  --background-color: white;
  --hover-background-color: #dde7ee;
  --light: #eaedf1;

  --map-width: v-bind(containerWidth + "px");

  --fixed-green: v-bind(fixedGreenColor);

  position: relative;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto 1fr;

  color: var(--text-color);
  background-color: var(--light);
  -webkit-tap-highlight-color: transparent;
}

/*set font*/
.app-root,
button,
input,
textarea,
select {
  font-family: GT Eesti, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  font-size: 16px;
  line-height: 1;
}

.app-root.small {
  font-size: 13px;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr 1fr;
}

/*.dark-theme .app-root {
  --text-color: #eff3f5;
  --background-color: #141414;
  --lightest: #373c45;
}*/

.app-root.geolocation-loading,
.app-root.loading {
  pointer-events: none;
}

.app-root main,
.app-root header,
.app-root aside {
  transition: filter 1s ease;
}

.app-root.geolocation-loading main,
.app-root.geolocation-loading header,
.app-root.geolocation-loading aside,
.app-root.loading main,
.app-root.loading header,
.app-root.loading aside {
  filter: grayscale(1) blur(8px);
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background-color: rgba(26 26 26 / 0.16);

  display: none;

  color: white;
  font-size: 9vmin;
  text-align: center;
  line-height: 1.15;

  filter: drop-shadow(1px 1px 0 var(--text-color));
}

.app-root.geolocation-loading .overlay,
.app-root.loading .overlay {
  display: flex;
  align-items: center;
  justify-content: center;
}

img {
  max-width: 100%;
}

header,
footer {
  grid-column: 1 / span 2;
}

.small header,
.small footer {
  grid-column: unset;
}

header {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr;
  background-color: var(--background-color);
  user-select: none;

  position: relative;
}

header .info-button {
  position: absolute;

  top: 0;
  right: 11px;
  height: 100%;
  width: 44px;

  padding: 0 4px;

  background: transparent;
  border: none;

  cursor: pointer;
}

.small header .info-button {
  width: 35px;
  right: 4px;
}

header .info-button:hover {
  background-color: var(--hover-background-color);
}

header > label {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  font-size: 1.75em;
  font-weight: 700;
  height: 1.75em;
}

header > label > input {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  margin: 0;
  padding: 0;

  cursor: pointer;
}

header > label > div {
  position: absolute;
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;

  border-top-right-radius: 1rem;
  border-top-left-radius: 1rem;
  color: var(--gray);
  transition: 150ms ease;

  pointer-events: none;
}

.small header > label > div {
  border-top-right-radius: 6px;
  border-top-left-radius: 6px;
}

.small header > label > div {
  transition: none;
}

header > label > input:active + div,
header > label > input:checked + div {
  color: var(--text-color);
  background-color: var(--light);
}

header > label > input:not(:disabled):not(:checked):hover + div {
  color: var(--text-color);
  background-color: var(--hover-background-color);
}

header > label > input:disabled + div {
  border-color: var(--gray);
  opacity: 0.25;
  cursor: not-allowed;
}

/*** map ***/

main {
  width: 100%;
  height: 100%;
  position: relative;
  outline: none;
}

.small main {
  grid-row: 2;
}

main section {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 999;
  display: flex;

  user-select: none;
}

main section label {
  background-color: rgba(255 255 255 / 0.55);
  backdrop-filter: brightness(1.25) blur(6px);

  font-weight: 700;
  font-size: 1em;

  display: flex;
  align-items: center;
  gap: 1ch;

  box-sizing: border-box;
  padding: 0.75em;
  border-radius: 0.5em;

  position: relative;
}

main section label input {
  margin: 0;
  padding: 0;
  margin-right: 1ch;

  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;

  opacity: 0;
  cursor: pointer;
}

main section label input ~ svg {
  height: 1.5em;

  fill: var(--gray);
}

main section label input:checked ~ svg {
  fill: var(--section-color);
}

main section label input:not(:checked) ~ div {
  color: var(--gray);
}

/*****/

aside {
  overflow-x: hidden;
  overflow-y: auto;

  display: flex;
  flex-direction: column;

  position: relative;
}

.is-read-mode.small aside {
  overflow-x: auto;
  overflow-y: hidden;

  flex-direction: row;
}

aside > section {
  /*  width: 100%;*/
  margin: 1rem;
}

.dropdown {
  width: 100%;
  max-width: 45vw;
  display: flex;
  flex-direction: column;
  gap: 3px;

  margin-bottom: 1.5em;

  font-weight: 700;
  color: var(--gray);
}

.small .dropdown {
  margin-bottom: 0.75em;
}

.save-form select,
.dropdown select {
  text-align: center;
  border: none;
  font-weight: 700;
  font-size: 1.25em;
  cursor: pointer;

  background-color: var(--text-color);
  color: var(--background-color);

  box-sizing: border-box;
  padding: 0.25em;

  border-radius: 4px;

  transition: 150ms ease;
}

.small .save-form select,
.small .dropdown select {
  transition: none;
}

.save-form select:hover,
.dropdown select:hover {
  color: var(--hover-background-color);
}

.category-input {
  margin-top: 1.5em;
}

.legend {
  width: calc(100% - 2rem);

  margin-bottom: 3em;

  font-size: 1.25em;

  border-top: 2px dotted var(--line);

  display: flex;
  flex-direction: column;
}

.small .legend {
  font-size: 1.1em;
}

.legend > div {
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  box-sizing: border-box;
  padding: 0.25em 0;

  border-bottom: 2px dotted var(--line);
}

.legend > div::before {
  content: "";
  font-weight: 700;
  width: 1em;
  height: 1em;
  margin-right: 1ch;
  margin-left: 1rem;

  box-sizing: border-box;
  background-color: white;
  border: 0.3em solid var(--c);
  border-radius: 50%;
}

.legend small {
  font-weight: 300;
  margin-left: auto;
  color: var(--gray);
}

.total {
  font-weight: 300;
  color: var(--gray);
  font-size: 0.9em;

  display: flex;
  align-items: center;
}

/*****/

aside button {
  cursor: pointer;
  border: none;
  border-radius: 1.75em;
  padding: 0.5em 1.5em;

  background-color: var(--text-color);
  color: var(--background-color);
  font-weight: 700;

  transition: 150ms ease;

  line-height: 1.1;

  user-select: none;
}

.small aside button {
  transition: none;
}

aside button:hover {
  background-color: var(--text-color);
  color: var(--hover-background-color);
}

aside button:disabled {
  background-color: var(--gray);
}

.big-button {
  font-size: clamp(1.1rem, calc(0.075 * var(--map-width)), 75px);

  padding: 0.75em 1.55em;
  width: 90%;

  align-self: center;
  justify-self: center;
  margin: auto;
}

.small .big-button {
  align-self: start;
  margin-top: 1em;
}

.geolocation-error {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  text-align: center;
  color: var(--gray);
  font-size: 0.67em;
  font-weight: 300;
}

/*****/

.arrow-button {
  position: fixed;
  z-index: 9999;
  left: 0;
  top: 75%;
  transform: translateY(-50%);
  height: 40%;

  width: 3em;

  color: var(--gray);

  display: flex;
  align-items: center;
  justify-content: flex-start;

  cursor: pointer;
}

.arrow-button:hover {
  color: var(--text-color);
}

.arrow-button svg {
  pointer-events: none;
  width: 70%;
}

.arrow-right {
  left: unset;
  right: 0;
  justify-content: flex-end;
}

/*****/

.save-form {
  display: flex;
  flex-direction: column;
  gap: 0.25em;

  margin: 0;
  padding-top: 1rem;
  padding-left: 1rem;
  padding-right: 1rem;
  padding-bottom: 3em;
  box-sizing: border-box;
}

.save-form h2 {
  margin: 0;
  font-size: 2em;
}

.save-form strong {
  display: flex;
  align-items: center;
  gap: 0.7ch;

  line-height: 1.25;
}

.save-form small {
  font-size: 1.1em;
  font-weight: 300;
  color: var(--gray);
}

.save-form label {
  display: flex;
  /*  align-items: center;*/
  /*  gap: 1ch;*/
  flex-direction: column;

  font-weight: 300;
  margin-bottom: 1em;

  font-size: 1.25em;
}

.save-form label input,
.save-form label textarea {
  flex-grow: 1;

  padding: 0.25em;

  font-size: 1.1em;

  background-color: var(--background-color);
  border: none;
  border-radius: 2px;
}

.image-input {
  position: relative;

  background-color: var(--text-color);
  color: var(--background-color);

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 1.5em;
  padding: 0.5em 1.5em;

  font-size: 1.75em;

  width: 50%;
  margin-left: auto;
  height: 2em;
}

.image-input:hover {
  color: var(--hover-background-color);
}

.image-input input {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  cursor: pointer;
}

.save-form .submit {
  margin: 1em 0;

  font-size: 2.25em;
}

.save-form input::placeholder,
.save-form textarea::placeholder {
  color: var(--gray);
  font-weight: 600;
  font-size: 1.1em;
}

.save-form img {
  margin-bottom: 1em;
}

/*****/

footer {
  border-top: 1px solid var(--line);

  padding: 2px;

  display: flex;
  flex-wrap: wrap;
  gap: 1em;

  background-color: var(--background-color);

  font-size: 0.9em;
  font-weight: 700;
}

footer > div,
footer > div:first-of-type > a {
  display: flex;
  align-items: center;
  gap: 0.7ch;
}

footer > div:first-of-type {
  margin-right: auto;
}

footer a {
  color: var(--gray);
  text-decoration: none;
}

footer a:not(:last-of-type)::after {
  content: ",";
}

footer a:hover {
  text-decoration: underline;
  text-decoration-color: #0064fa;
}

/*****/

@supports (mix-blend-mode: color-burn) {
  .osmOverlay .map::after {
    content: "";

    pointer-events: none;

    position: absolute;
    z-index: 999;

    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    background-color: var(--gray);
    opacity: 0.3;
    mix-blend-mode: color-burn;
  }

  /*.dark-theme .osmOverlay .map::after {
    opacity: 0.6;
  }*/
}
</style>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  inject,
  nextTick,
  watch,
} from "vue";

import InfoPopup from "./components/InfoPopup";
import MarkerCard from "./components/MarkerCard.vue";
import PM from "./components/PM.vue";

import { useGeolocation } from "@vueuse/core";
import imageCompression from "browser-image-compression";
import { isPointInPolygon } from "geolib";
import gsap from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

import eestiGeoData from "./data/eesti.json";
import { initMapTiles } from "./tiles";
import { api } from "./api";

gsap.registerPlugin(ScrollToPlugin);

const headerMenu = [
  { label: "Märgi", value: "write" },
  { label: "Sirvi", value: "read" },
];

const showInfo = ref(false);

//
const isLoading = ref(true);
const markersData = ref(null);
const categoriesData = ref(null);

const allDates = computed(() => {
  if (!markersData.value) {
    return [];
  }

  return [...new Set(markersData.value.map((item) => item.dateString))];
});

const selectedDate = ref(null);

// "Parandatud"
const fixedGreenColor = "#4C988C";

//
const appState = ref(null);
const userFilledData = ref({
  category_id: 1,
});
const imageDataBase64 = ref(null);

//
const containerElement = inject("containerElement");
const containerWidth = ref(0);
const isSmall = computed(() => {
  return containerWidth.value < 780;
});

const asideElement = ref(null); // template ref
const asideScrollX = ref(0);
const showArrowLeft = computed(() => {
  if (!asideElement.value) {
    return false;
  }
  return asideScrollX.value > 0.25 * containerWidth.value;
});
const showArrowRight = computed(() => {
  if (!asideElement.value) {
    return false;
  }
  if (markersDataFiltered.value.length === 0) {
    return false;
  }
  return (
    asideScrollX.value === 0 ||
    asideScrollX.value <
      asideElement.value.scrollWidth - asideElement.value.clientWidth - 5
  );
});

//
const mapContainer = ref(null);
const EST_CENTER = [58.68485, 24.986737];
let map; // L.map
const currentLayer = ref("OpenStreetMap");

//
const totalCountys = eestiGeoData.features.length;
const countysOnMap = ref({}); // eestiGeoData grouped by MNIMI
let countysGroup;
const selectedCounty = ref(null);

const markersDataFiltered = computed(() => {
  if (!markersData.value) {
    return [];
  }
  let m = markersData.value;

  if (selectedCounty.value !== "k6ik") {
    m = m.filter((item) => {
      return item.county === selectedCounty.value;
    });
  }

  if (selectedDate.value === "n2dal") {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);
    weekAgo.setHours(0, 0, 0, 0);
    console.log(today, weekAgo);
    m = m.filter((item) => {
      return item.dateObject >= weekAgo && item.dateObject <= today;
    });
  } else if (selectedDate.value !== "k6ik") {
    m = m.filter((item) => {
      return item.dateString === selectedDate.value;
    });
  }

  return m;
});

// pip
const eestiMaakonnadPolygons = {};
eestiGeoData.features.forEach((feature) => {
  eestiMaakonnadPolygons[feature.properties.MNIMI] =
    feature.geometry.coordinates; // lng, lat !!!
});

const findCounty = (latlng) => {
  // console.log("findCounty", latlng);
  for (const maakond in eestiMaakonnadPolygons) {
    const polygons = eestiMaakonnadPolygons[maakond];
    for (const polygon of polygons) {
      // console.log(polygon);
      if (isPointInPolygon(latlng, polygon)) {
        return maakond;
      } else {
        for (const subPolygon of polygon) {
          // console.log(subPolygon);
          if (isPointInPolygon(latlng, subPolygon)) {
            return maakond;
          }
        }
      }
    }
  }
  return "ei leia";
};

// read markers (juba märgitud punktid)
let readMarkersOnMap = {};
const readMarkersGroup = L.featureGroup([]);

// write marker (märgi uus)
let writeMarkerOnMap;

// vue-use geolocation
const {
  locatedAt,
  coords: geolocationCoords,
  error: geolocationError,
  resume: geolocationResume,
  pause: geolocationPause,
} = useGeolocation({
  enableHighAccuracy: true,
});

const isUsingGeolocation = ref(true);
const geolocationPPError = ref(false); // permissions policy error (iframe)

const geolocationCoordsArray = computed(() => {
  if (geolocationCoords.value) {
    const { latitude, longitude } = geolocationCoords.value;
    if (
      !latitude ||
      !longitude ||
      latitude === Infinity ||
      longitude === Infinity
    ) {
      return null;
    }
    return [latitude, longitude];
  } else {
    return null;
  }
});

const createWriteMarker = (coords) => {
  if (!map) {
    return;
  }
  // console.log("createWriteMarker", coords);

  // L.marker(coords, { zIndexOffset: 1000 }).addTo(map); // test

  writeMarkerOnMap = L.marker(coords, {
    icon: L.divIcon({
      className: "marker-icon write-marker",
      html: `<svg viewBox="0 0 28 36">
          <path d="M14 0c7.732 0 14 5.641 14 12.6C28 23.963 14 36 14 36S0 24.064 0 12.6C0 5.641 6.268 0 14 0Z" />
          <circle cx="14" cy="14" r="5.55" />
        </svg>
        <div class="pulse"></div>
      `,
      iconSize: null,
      interactive: false,
    }),
  }).addTo(map);
};

const onNewGeolocation = () => {
  if (appState.value === "write" && isUsingGeolocation.value) {
    const coords = geolocationCoordsArray.value;
    if (!coords) {
      console.log("onNewGeolocation returning, no coordinates");
      return;
    }

    console.log(`onNewGeolocation ${coords[0]}, ${coords[1]}`);

    if (writeMarkerOnMap) {
      writeMarkerOnMap.setLatLng(coords);
    } else {
      createWriteMarker(coords);
    }

    // follow the marker
    nextTick(() => {
      if (map.getZoom() < 14) {
        map.setView(coords, 16);
      } else {
        map.setView(coords);
      }
    });
  }
};

// ***

watch(geolocationCoords, onNewGeolocation);

watch(isUsingGeolocation, () => {
  if (isUsingGeolocation.value) {
    geolocationResume();
    onNewGeolocation(); // switched on
  } else {
    geolocationPause();
  }
});

watch(geolocationError, () => {
  // console.log(geolocationError.value);
  if (geolocationError.value) {
    isUsingGeolocation.value = false;

    const msg = geolocationError.value.message;

    if (msg.includes("permissions policy") || msg.includes("denied prompt")) {
      geolocationPPError.value = true; // iframe
    }

    if (msg.includes("User denied Geolocation")) {
      window.alert("Positsioneerimiseks palun lubada seadme asukoht");
    }
  }
});

/**
 *    App state change
 */
watch(appState, async (newState) => {
  console.log("💡 STATE", newState);

  if (newState === "save") {
    geolocationPause();

    // is saving
    map.keyboard.disable();
    map.dragging.disable();
    map.doubleClickZoom.disable();
    map.touchZoom.disable();
    map.boxZoom.disable();
    map.scrollWheelZoom.disable();
    map.invalidateSize();

    // prepare user filled data
    const coordsLatLng = writeMarkerOnMap.getLatLng();
    userFilledData.value.lat = coordsLatLng.lat;
    userFilledData.value.lng = coordsLatLng.lng;
    userFilledData.value.county = findCounty(coordsLatLng);
    userFilledData.value.title = "";
    // console.log("userFilledData", userFilledData.value);

    await nextTick();
    map.setView(coordsLatLng);
  } else {
    // clear markers
    readMarkersGroup.clearLayers(); // this should dispose all markers
    readMarkersOnMap = {}; // clear refrences

    // todo: revisit clear and reset markers

    //
    if (newState === "write") {
      //
      let coords;
      if (isUsingGeolocation.value) {
        geolocationResume();

        if (locatedAt.value) {
          const { latitude, longitude } = geolocationCoords.value;
          coords = [latitude, longitude];
        } else {
          coords = null;
        }
      } else {
        coords = map.getCenter();
      }

      if (coords) {
        if (!writeMarkerOnMap) {
          createWriteMarker(coords);
        } else {
          writeMarkerOnMap.setLatLng(coords);
        }

        await nextTick();

        if (isUsingGeolocation.value) {
          map.setView(coords, 16);
        } else {
          map.setZoom(11);
        }
      }

      //
    } else {
      // is reading
      geolocationPause();

      if (writeMarkerOnMap) {
        writeMarkerOnMap.remove();
        writeMarkerOnMap = null;
      }

      await nextTick();

      if (!selectedCounty.value) {
        selectedCounty.value = "k6ik"; // this will call resetMarkers in watcher!
      } else {
        resetMarkers();

        const feature = countysOnMap.value[selectedCounty.value];

        await nextTick();

        if (feature) {
          map.fitBounds(feature.getBounds());
        } else {
          map.setView(EST_CENTER, 6);
        }
      }
    }

    await nextTick();

    // is reading or writing
    map.keyboard.enable();
    map.dragging.enable();
    map.doubleClickZoom.enable();
    map.touchZoom.enable();
    map.boxZoom.enable();
    map.scrollWheelZoom.enable();
    map.invalidateSize();
  }

  // console.log(".");
  await nextTick();
  isLoading.value = false;
});

watch(selectedDate, () => {
  if (appState.value === "read") {
    resetMarkers();
  }
});

watch(selectedCounty, () => {
  console.log("selectedCounty", selectedCounty.value);

  if (appState.value === "read") {
    resetMarkers();
  }

  const c = selectedCounty.value;

  if (c === "k6ik") {
    nextTick(() => {
      map.setView(EST_CENTER, 6);
    });
  } else {
    const feature = countysOnMap.value[c];
    if (feature) {
      nextTick(() => {
        map.fitBounds(feature.getBounds());
      });
    }
  }
});

watch(isSmall, () => {
  if (asideElement.value) {
    asideElement.value.scrollLeft = 0;
  }
});

// ***

/**
 *
 *   app is mounted, init Leaflet map, controls, ..
 *
 */
function initMap() {
  const { osmTiles, maaametTiles, maaametFotoTiles } = initMapTiles(L);

  map = L.map(mapContainer.value, {
    // containerElement.querySelector(".map")
    onMapReady(map) {
      //
    },
    zoomControl: false, // add manually
    attributionControl: false, // add out of map
    minZoom: 6,
    zoom: 6,
    center: EST_CENTER,
    layers: [osmTiles],
    preferCanvas: true,
  });

  L.control.scale({ imperial: false }).addTo(map);

  L.control
    .layers(
      {
        OpenStreetMap: osmTiles,
        "Maa-amet": maaametTiles,
        Aerofoto: maaametFotoTiles,
      }, // base
      null, // overlay
      { position: "topright" }
    )
    .addTo(map);

  L.control
    .zoom({
      position: "bottomright",
    })
    .addTo(map);

  // Eesti maakonnad
  countysGroup = L.featureGroup().addTo(map);

  const countyFeatureOpts = {
    color: "#6f799a",
    weight: 1,
    opacity: 0.75,
    fillOpacity: 0,
    interactive: false,
  };
  eestiGeoData.features.forEach((geoJsonFeature) => {
    const name = geoJsonFeature.properties.MNIMI;
    const featureOnMap = L.geoJSON(geoJsonFeature, countyFeatureOpts);
    featureOnMap.addTo(countysGroup);
    countysOnMap.value[name] = featureOnMap;
  });

  //
  readMarkersGroup.addTo(map);

  // map events
  map.on("baselayerchange", (e) => {
    // console.log("baselayerchange", e);
    currentLayer.value = e.name;
  });

  map.on("move", (e) => {
    if (
      appState.value === "write" &&
      writeMarkerOnMap &&
      !isUsingGeolocation.value
    ) {
      // console.log("move", e);

      writeMarkerOnMap.setLatLng(map.getCenter());
    }
  });

  /*map.on("click", (e) => {
    const { latlng } = e;
    const { lat, lng } = latlng;
    const county = findCounty(latlng);
    console.log(`${lat}, ${lng} @ ${county}`);
  });*/

  //
  nextTick(() => {
    map.invalidateSize();
  });
}

/**
 *
 */
async function fetchData() {
  isLoading.value = true;

  try {
    const { markers: m, categories: c } = await api.get("widget");

    const categoriesById = {};
    c.forEach((cat) => {
      categoriesById[cat.id] = cat;
    });

    m.reverse(); // :-/ will fix later

    m.forEach((item) => {
      const cat = categoriesById[item.category_id];
      item.color = cat.color;
      item.categoryTitle = cat.title;

      const date = new Date(item.created);
      item.dateObject = date;
      item.dateString = date.toLocaleDateString("et", {
        weekday: "short",
        day: "numeric",
        month: "long",
      });
    });

    markersData.value = m;
    categoriesData.value = c;
    // console.log("markersData", markersData.value);
    // console.log("categoriesData", categoriesData.value);

    selectedDate.value = allDates.value[0];

    //
  } catch (e) {
    console.log(e);
  } finally {
    isLoading.value = false;
  }
}

/**
 *
 */
async function resetMarkers() {
  console.log("resetMarkers");

  // clear
  readMarkersGroup.clearLayers(); // this should dispose all markers
  readMarkersOnMap = {}; // clear refrences

  await nextTick();

  // create new markers
  for (const item of markersDataFiltered.value) {
    const { id, lat, lng, title, color, is_fixed, category_id } = item;

    // L.marker
    const marker = L.marker([lat, lng], {
      icon: L.divIcon({
        className: `marker-icon marker-icon-${id}`,
        html: `
          <img src='img/marker-${is_fixed ? "green" : category_id}.png' />
        `,
        iconSize: null,
      }),
    }).addTo(readMarkersGroup);

    marker.bindTooltip(title);

    marker.on("click", () => {
      const element = document.querySelector(`[data-marker-id='${id}']`);

      const scrollAnimationOpts = {
        duration: 0.5,
        ease: "sine.out",
        // scrollTo: element,
      };

      if (isSmall.value) {
        scrollAnimationOpts.scrollTo = {
          x: element,
          offsetX: 0.5 * (asideElement.value.clientWidth - element.clientWidth),
        };
      } else {
        scrollAnimationOpts.scrollTo = {
          y: element,
          offsetY:
            0.5 * (asideElement.value.clientHeight - element.clientHeight),
        };
      }

      gsap.to(asideElement.value, scrollAnimationOpts);

      gsap.fromTo(
        element,
        {
          filter: "brightness(1.25)",
        },
        {
          filter: "brightness(1)",
          duration: 1.5,
          delay: 0.5,
          ease: "sine.out",
        }
      );

      // console.log("scroll to", id, element);
    });

    readMarkersOnMap[id] = marker;

    //
    /*const testPosition = L.marker([lat, lng], {
      zIndexOffset: 1000,
    }).addTo(map);*/
  }
}

/**
 *
 */
function showMarkerOnMap(item) {
  // console.log("show", item);

  try {
    Object.values(readMarkersOnMap).forEach((m) => {
      m.closeTooltip();
    });

    const m = readMarkersOnMap[item.id];

    if (m) {
      map.setView(m.getLatLng());

      const markerElement = document.querySelector(`.marker-icon-${item.id}`);
      const allMarkerElements = document.querySelectorAll(".marker-icon");

      gsap.fromTo(
        markerElement,
        {
          filter: "brightness(1.25)",
        },
        {
          filter: "brightness(1)",
          duration: 2.2,
          delay: 0.1,
          ease: "sine.out",
        }
      );

      allMarkerElements.forEach((el) => {
        el.style["z-index"] = 100;
      });

      markerElement.style["z-index"] = 101;

      nextTick(() => {
        m.openTooltip();
      });
    }
  } catch (e) {
    console.log(e);
  }
}

/**
 *
 */
async function markMarkerFixed(item) {
  // console.log("fix", item);

  if (window.confirm(`${item.title} on kordatehtud?`)) {
    try {
      isLoading.value = true;

      const responseData = await api.url("marker/fix/" + item.id).post();
      console.log(responseData);

      item.is_fixed = true;

      nextTick(() => {
        resetMarkers();
      });
    } catch (e) {
      console.log(e);
      window.alert("🐟 Viga!");
    } finally {
      isLoading.value = false;
    }
  }

  try {
    //
  } catch (e) {
    console.log(e);
  }
}

/**
 *    input type file
 */
async function onFileInput(e) {
  // console.log(e);
  const imageFile = e.target.files[0];
  const compressedFile = await imageCompression(imageFile, {
    maxSizeMB: 2,
    maxWidthOrHeight: 1000,
    fileType: "image/jpeg",
    useWebWorker: true,
  });
  imageDataBase64.value = await imageCompression.getDataUrlFromFile(
    compressedFile
  );
}

/**
 *    Märgi uus punkt
 */
async function submitMarker() {
  // validate
  if (!userFilledData.value.title) {
    userFilledData.value.title = new Date().toLocaleTimeString("et", {
      weekday: "short",
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  isLoading.value = true;

  try {
    const { lat, lng, title, description, county, category_id } =
      userFilledData.value;

    if (!lat || !lng) {
      console.log(userFilledData.value);
      window.alert("🐟 Viga!");
      return;
    }

    const data = {
      lat,
      lng,
      title,
      description,
      county,
      category_id,
      imageDataBase64: imageDataBase64.value,
    };
    console.log("submit", data);

    const responseData = await api.url("marker").post(data);
    console.log(responseData);

    window.alert("✅ Märkimine oli edukas!");

    fetchData().then(() => {
      appState.value = "read";
    });

    //
  } catch (e) {
    console.log(e);
    window.alert("🐟 Viga!");
  } finally {
    isLoading.value = false;
  }
}

/**
 *
 */
function cancelMarking() {
  appState.value = "write";
  imageDataBase64.value = null;
  userFilledData.value = {
    category_id: 1,
  };
}

/**
 *
 */
function openInNewTab() {
  window.open(window.location.href.split("?")[0], "_blank");
}

/**
 *
 */
function scrollLeft() {
  // console.log("scrollLeft");
  gsap.to(asideElement.value, {
    duration: 0.5,
    ease: "sine.out",
    scrollTo: {
      x: asideElement.value.scrollLeft - 0.75 * containerWidth.value,
    },
  });
}

/**
 *
 */
function scrollRight() {
  // console.log("scrollRight");
  gsap.to(asideElement.value, {
    duration: 0.5,
    ease: "sine.out",
    scrollTo: {
      x: asideElement.value.scrollLeft + 0.75 * containerWidth.value,
    },
  });
}

/**
 *
 */
function onAsideScroll() {
  asideScrollX.value = asideElement.value.scrollLeft;
  // console.log(asideScrollX.value);
}

/**
 *
 */
function onResize() {
  containerWidth.value = containerElement.clientWidth;
}

/**
 *    Vue lifecycle
 */
onMounted(() => {
  initMap();
  fetchData()
    .then(() => {
      // url parameters
      const urlParams = new URLSearchParams(window.location.search);

      const stateParam = urlParams.get("state");
      appState.value =
        stateParam && ["read", "write"].includes(stateParam)
          ? stateParam
          : "write";

      const countyParam = urlParams.get("county");
      if (countyParam) {
        const allCountys = Object.keys(countysOnMap.value);
        // console.log(countyParam, allCountys);
        for (const countyName of allCountys) {
          if (countyName.includes(countyParam)) {
            selectedCounty.value = countyName;
          }
        }
      }
    })
    .catch((e) => {
      console.log("ERROR", e);
    });

  window.addEventListener("resize", onResize);
  onResize();

  onAsideScroll();
});

onUnmounted(() => {
  window.removeEventListener("resize", onResize);
});

/*****
 *  *  🐟
 ***/
</script>
